import React, { useState, useEffect } from "react";
import axiosClient from "../utils/apiClient";
import { useParams, useNavigate } from "react-router-dom";
import { BiSave, BiXCircle, BiCreditCard } from "react-icons/bi";
import Select from "react-select"; 


const InvoiceForm = () => {
  const { id } = useParams(); // invoice id for edit mode
  const isEditing = !!id;
  const navigate = useNavigate();

  const today = new Date().toISOString().split("T")[0];

  const [clients, setClients] = useState([]);
  const [cases, setCases] = useState([]);

  const [formData, setFormData] = useState({
    invoice_number: "",
    client_id: "",
    case_id: "",
    issue_date: today,
    due_date: today,
    total_amount: "",
    invoice_details: "", // <-- ADDED: Initialize the new field
    send_email: false,
  });

  const [clientEmail, setClientEmail] =useState(null)

  const [loading, setLoading] = useState(true);
  const [submitStatus, setSubmitStatus] = useState("");
  const [message, setMessage] = useState("");
  const [stripeConnectedId, setStripeConnectedId] = useState("");

  const user = JSON.parse(localStorage.getItem("user"));

  // Load Stripe ID safely
  useEffect(() => {
    const stripeId = localStorage.getItem("stripeAccountId");
    if (stripeId) setStripeConnectedId(stripeId);
  }, []);


  useEffect(()=>{

    if(clients.length>0){
      if(formData.client_id){
        const client = clients.find(e => e.id == formData.client_id);
        setClientEmail(client.email)
      }
    }

  },[formData.client_id])

  // Fetch clients, cases, and invoice (if editing)
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [clientRes, caseRes] = await Promise.all([
          axiosClient.get("/clients"), 
          axiosClient.get("/cases/all"),
        ]);

        setClients(clientRes.data.data || []);
        setCases(caseRes.data.data || []);
        
        if (isEditing) {
          const invRes = await axiosClient.get(`/invoices/${id}`);
          const inv = invRes.data;

          setFormData({
            invoice_number: inv.invoice_number || "",
            client_id: inv.client_id || "",
            case_id: inv.case_id || "",
            issue_date: inv.issue_date?.split("T")[0] || today,
            due_date: inv.due_date?.split("T")[0] || today,
            total_amount: inv.total_amount || "",
            invoice_details: inv.invoice_details || "", // <-- ADDED: Load existing details
            send_email: inv.send_email || false,
          });
        }
      } catch (err) {
        console.error("Invoice form fetch error:", err);
        setMessage("Failed to load form data.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, isEditing, today]);

  // Handle regular field changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Stripe connect
  const handleStripeConnect = async () => {
    try {
      const token = localStorage.getItem("token");

      const res = await axiosClient.get(`/stripe/connect`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (res.data?.url) {
        window.location.href = res.data.url;
      } else {
        alert("Unable to start Stripe connection.");
      }
    } catch (err) {
      console.error("Stripe connect error:", err);
      alert("Failed to connect Stripe account.");
    }
  };

  // Submit (Create or Update)
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitStatus("submitting");

    try {
      let response;

      if (isEditing) {
        response = await axiosClient.put(`/invoices/${id}`, formData);
      } else {
        response = await axiosClient.post("/invoices", formData);
      }

      setSubmitStatus("success");
      setMessage(`Invoice ${isEditing ? "updated" : "created"} successfully!`);

      setTimeout(() => navigate("/invoices"), 1200);
    } catch (err) {
      console.error("Submit error:", err);
      setSubmitStatus("error");
      setMessage(
        err.response?.data?.message || "Failed to submit invoice. Try again."
      );
    }
  };

  if (loading) return <div className="p-6 text-center">Loading...</div>;

  return (
    <div className="max-w-3xl mx-auto p-4">
      <h1 className="text-3xl font-bold text-gray-800 mb-6">
        {isEditing ? "Edit Invoice" : "Create Invoice"}
      </h1>

      {/* Admin Stripe Connect Box */}
      {user?.roleName === "admin" && !stripeConnectedId && (
        <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg shadow-sm flex justify-between items-center mt-6 mb-6">
          <div>
            <p className="text-blue-800 font-semibold">Stripe Connect</p>
            <p className="text-sm text-blue-600">
              {stripeConnectedId
                ? "Stripe Account Connected: " + stripeConnectedId
                : "Connect your Stripe account to receive payments."}
            </p>
          </div>

          <button
            onClick={handleStripeConnect}
            className="flex items-center bg-blue-600 text-white px-4 py-2 rounded-lg shadow hover:bg-indigo-700 transition"
          >
            <BiCreditCard className="mr-2 h-5 w-5" />
            {stripeConnectedId ? "Change Stripe Account" : "Connect Stripe"}
          </button>
        </div>
      )}

      <form
        onSubmit={handleSubmit}
        className="bg-white p-6 shadow-sm rounded-lg space-y-6"
      >
        {/* Invoice Number */}
        <div>
          <label className="block text-sm font-medium text-gray-700">
            Invoice Number (optional)
          </label>
          <input
            type="text"
            name="invoice_number"
            value={formData.invoice_number}
            onChange={handleChange}
            placeholder="Leave empty to auto-generate"
            className="mt-1 p-2 w-full border rounded-lg"
          />
        </div>

        {/* CLIENT SELECTOR (SEARCHABLE) */}
        <div>
          <label className="block text-sm font-medium text-gray-700">
            Client
          </label>

          <Select
            options={clients.map((c) => ({
              value: c.id,
             label: c.agency
                                    ? `${c.agency}${c.first_name && ' (' } ${c.first_name } ${c.last_name == 'NA' ? "":c.last_name}${c.first_name && ')' }`
                                    : `${c.first_name} ${c.last_name == 'NA' ? "":c.last_name}`,
              client: c,
            }))}
            value={
              formData.client_id
                ? clients
                    .map((c) => ({
                      value: c.id,
                      label: c.agency
                                    ? `${c.agency}${c.first_name && ' (' } ${c.first_name } ${c.last_name == 'NA' ? "":c.last_name}${c.first_name && ')' }`
                                    : `${c.first_name} ${c.last_name == 'NA' ? "":c.last_name}`,
                      client: c,
                    }))
                    .find((opt) => opt.value === formData.client_id)
                : null
            }
            onChange={(selected) => {
              setFormData((prev) => ({
                ...prev,
                client_id: selected.value,
              }));
            }}
            placeholder="Search client..."
            className="mt-1"
            classNamePrefix="react-select"
            isSearchable
          />
        </div>

        {/* Case */}
        <div>
          <label className="block text-sm font-medium text-gray-700">
            Case
          </label>
          <select
            name="case_id"
            value={formData.case_id}
            onChange={handleChange}
            required
            className="mt-1 p-2 w-full border rounded-lg"
          >
            <option value="">Select case</option>
            {cases.map((cs) => (
              <option key={cs.id} value={cs.id}>
                {cs.case_name} ({cs.case_number})
              </option>
            ))}
          </select>
        </div>

        {/* Dates */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium">Issue Date</label>
            <input
              type="date"
              name="issue_date"
              value={formData.issue_date}
              onChange={handleChange}
              required
              className="mt-1 p-2 w-full border rounded-lg"
            />
          </div>

          <div>
            <label className="block text-sm font-medium">Due Date</label>
            <input
              type="date"
              name="due_date"
              value={formData.due_date}
              min={formData.issue_date}
              onChange={handleChange}
              required
              className="mt-1 p-2 w-full border rounded-lg"
            />
          </div>
        </div>

        {/* Amount */}
        <div>
          <label className="block text-sm font-medium">Total Amount ($)</label>
          <input
            type="number"
            name="total_amount"
            value={formData.total_amount}
            onChange={handleChange}
            required
            className="mt-1 p-2 w-full border rounded-lg"
          />
        </div>

        {/* NEW INVOICE DETAILS FIELD */}
        <div>
          <label className="block text-sm font-medium text-gray-700">
            Invoice Details (Optional itemization)
          </label>
          <textarea
            name="invoice_details"
            value={formData.invoice_details}
            onChange={handleChange}
            rows="5"
            placeholder={`Rush Service - $75\nMileage (25 Miles) - $16.75\nDocument Retrieval - $25`}
            className="mt-1 p-2 w-full border rounded-lg focus:ring-blue-500 focus:border-blue-500"
          ></textarea>
        </div>

        {/* Send Email Checkbox */}
        <div>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              name="send_email"
              checked={formData.send_email}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  send_email: e.target.checked,
                }))
              }
              className="h-4 w-4"
            />
            Send This Invoice To Client Email
              { clientEmail ? ` : ${clientEmail}`: ""}
            
          </label>
        </div>

        {/* Buttons */}
        <div className="flex justify-end space-x-4">
          <button
            type="button"
            onClick={() => navigate("/invoices")}
            className="px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300"
          >
            <BiXCircle className="inline-block mr-2" /> Cancel
          </button>

          <button
            type="submit"
            disabled={submitStatus === "submitting"}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            <BiSave className="inline-block mr-2" />
            {isEditing ? "Update Invoice" : "Create Invoice"}
          </button>
        </div>

        {submitStatus === "success" && (
          <p className="text-green-600 text-center">{message}</p>
        )}
        {submitStatus === "error" && (
          <p className="text-red-600 text-center">{message}</p>
        )}
      </form>
    </div>
  );
};

export default InvoiceForm;
